// Lume — movimento.
//
// Duas coisas:
// 1. Revelar elementos ao entrar na tela, reproduzindo os appearEffect do
//    projeto Framer (fade subindo 42px, entradas laterais de 35/45px e
//    escala 0.95), com o mesmo escalonamento de 0,15s entre irmãos.
// 2. Controlar a velocidade do vídeo do hero, que o Liquid não expõe.
//
// O elemento só fica escondido depois que este script roda. Assim, se o JS
// falhar ou o visitante pedir menos movimento, tudo aparece normalmente.

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

// No Horizon quem rola no desktop é a div .page-wrapper (o body fica com
// overflow hidden); no celular é a janela. Ler window.scrollY direto zera
// no desktop, então tudo que depende da rolagem passa por aqui.
function lumeScrollTop() {
  const wrapper = document.querySelector('.page-wrapper');
  return Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
}

function lumeAoRolar(fn) {
  window.addEventListener('scroll', fn, { passive: true });
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper) wrapper.addEventListener('scroll', fn, { passive: true });
  return () => {
    window.removeEventListener('scroll', fn);
    if (wrapper) wrapper.removeEventListener('scroll', fn);
  };
}

function setupReveal() {
  const targets = document.querySelectorAll('[data-lume-rv]:not([data-lume-rv-ready])');
  if (targets.length === 0) return;

  if (REDUCED.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.setAttribute('data-lume-rv-ready', 'shown'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-lume-rv-ready', 'shown');
        observer.unobserve(entry.target);
      });
    },
    // O Framer dispara com metade do elemento visível. A margem negativa
    // embaixo evita que algo já colado no rodapé da janela dispare cedo demais.
    { threshold: 0.5, rootMargin: '0px 0px -5% 0px' }
  );

  targets.forEach((el) => {
    el.setAttribute('data-lume-rv-ready', 'hidden');
    // Já visível no carregamento (caso do hero): revela sem esperar scroll.
    const box = el.getBoundingClientRect();
    if (box.top < window.innerHeight * 0.85 && box.bottom > 0) {
      requestAnimationFrame(() => el.setAttribute('data-lume-rv-ready', 'shown'));
      return;
    }
    observer.observe(el);
  });
}

class LumeVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    const rate = parseFloat(this.dataset.rate);
    if (!Number.isNaN(rate) && rate > 0) {
      const apply = () => {
        video.playbackRate = rate;
      };
      apply();
      // O Safari redefine a taxa ao (re)carregar os metadados.
      video.addEventListener('loadedmetadata', apply);
      video.addEventListener('play', apply);
    }

    if (this.dataset.loop === 'false') {
      video.loop = false;
      video.removeAttribute('loop');
    }
  }
}

if (!customElements.get('lume-video')) customElements.define('lume-video', LumeVideo);

// Header: troca "Header Transparent" por "Header Fill" depois do primeiro
// scroll e abre/fecha a pílula do celular (variante "Aberto" do Framer).
// data-oculto: some rolando para baixo e volta num toque para cima (celular, como a Oura).
class LumeHeader extends HTMLElement {
  connectedCallback() {
    const limite = parseInt(this.dataset.threshold, 10) || 0;
    let ancora = lumeScrollTop();

    this.aoRolar = () => {
      const y = lumeScrollTop();
      this.dataset.scrolled = y > limite ? 'true' : 'false';
      // a âncora só anda depois de 6px, para o tremido do dedo não piscar o header
      if (y <= Math.max(limite, 60)) this.dataset.oculto = 'false';
      else if (y - ancora > 6) this.dataset.oculto = 'true';
      else if (ancora - y > 6) this.dataset.oculto = 'false';
      else return;
      ancora = y;
    };
    this.aoRolar();
    this.soltarRolagem = lumeAoRolar(this.aoRolar);

    // A sacola abre a gaveta do carrinho do Horizon (#cart-drawer). Sem a gaveta
    // (na própria página /cart ela não é renderizada), o link segue para /cart.
    this.querySelectorAll('a[data-lume-carrinho]').forEach((link) => {
      link.addEventListener('click', (evento) => {
        const gaveta = document.getElementById('cart-drawer');
        if (!gaveta || typeof gaveta.open !== 'function') return;
        evento.preventDefault();
        if (this.alternar && this.querySelector('.lume-header__bar--mob')?.dataset.open === 'true') this.alternar();
        gaveta.open();
      });
    });

    // Submenus no computador (como a Oura): o mouse no item abre o painel e a barra desce;
    // sair da barra fecha. Clique e Esc também funcionam (teclado e touch).
    const desk = this.querySelector('.lume-header__bar--desk');
    if (desk) {
      const itens = [...desk.querySelectorAll('[data-painel]:not(.lume-header__painel)')];
      const paineis = [...desk.querySelectorAll('.lume-header__painel')];
      let espera = 0;
      const abre = (n) => {
        clearTimeout(espera);
        if (n) { desk.dataset.painel = n; this.dataset.painel = n; } else { delete desk.dataset.painel; delete this.dataset.painel; }
        itens.forEach((b) => b.setAttribute('aria-expanded', String(b.dataset.painel === n)));
        paineis.forEach((p) => p.classList.toggle('on', p.dataset.painel === n));
      };
      this.fechaPainel = () => abre(null);
      itens.forEach((b) => {
        b.addEventListener('mouseenter', () => abre(b.dataset.painel));
        b.addEventListener('click', () => abre(desk.dataset.painel === b.dataset.painel ? null : b.dataset.painel));
      });
      // item sem submenu fecha o painel aberto
      desk.querySelectorAll('.lume-header__menus a').forEach((a) => a.addEventListener('mouseenter', () => abre(null)));
      desk.addEventListener('mouseleave', () => { espera = setTimeout(() => abre(null), 150); });
      desk.addEventListener('mouseenter', () => clearTimeout(espera));
      this.querySelector('.lume-header__veu')?.addEventListener('click', () => abre(null));
    }

    const barra = this.querySelector('.lume-header__bar--mob');
    const botao = barra && barra.querySelector('.lume-header__toggle');
    const menu = barra && barra.querySelector('.lume-header__menus');
    if (!barra || !botao || !menu) return;

    const rotulo = botao.querySelector('.lume-header__toggle-label');

    // Celular: item com submenu abre uma mini página no próprio menu, com "voltar" no lugar da logo.
    const nivel0 = menu.querySelector('.lume-header__nivel:not(.lume-header__nivel--sub)');
    const subs = [...menu.querySelectorAll('.lume-header__nivel--sub')];
    const mostraSub = (n) => {
      if (n) barra.dataset.sub = n; else delete barra.dataset.sub;
      if (nivel0) nivel0.hidden = !!n;
      subs.forEach((el) => { el.hidden = el.dataset.sub !== n; });
    };
    menu.querySelectorAll('button[data-sub]').forEach((b) => b.addEventListener('click', () => mostraSub(b.dataset.sub)));
    barra.querySelector('.lume-header__voltar')?.addEventListener('click', () => mostraSub(null));

    this.alternar = () => {
      const aberto = barra.dataset.open !== 'true';
      if (!aberto) mostraSub(null);
      barra.dataset.open = aberto ? 'true' : 'false';
      botao.setAttribute('aria-expanded', String(aberto));
      menu.hidden = !aberto;
      if (rotulo) rotulo.textContent = aberto ? rotulo.dataset.fechar : rotulo.dataset.abrir;
      botao.setAttribute('aria-label', aberto ? 'Fechar menu' : 'Abrir menu');
      // o menu aberto ocupa a tela inteira: a página atrás não rola
      document.documentElement.style.overflow = aberto ? 'hidden' : '';
      document.body.style.overflow = aberto ? 'hidden' : '';
    };
    botao.addEventListener('click', this.alternar);

    // Esc fecha, como qualquer menu sobreposto.
    this.aoTeclar = (evento) => {
      if (evento.key === 'Escape' && this.fechaPainel) this.fechaPainel();
      if (evento.key === 'Escape' && barra.dataset.open === 'true') {
        this.alternar();
        botao.focus();
      }
    };
    document.addEventListener('keydown', this.aoTeclar);
  }

  disconnectedCallback() {
    if (this.soltarRolagem) this.soltarRolagem();
    if (this.aoTeclar) document.removeEventListener('keydown', this.aoTeclar);
  }
}

if (!customElements.get('lume-header')) customElements.define('lume-header', LumeHeader);

// Quick Buy: a pílula de compra só aparece depois que o visitante passa do
// bloco de compra do topo — as variantes "Ativo"/"Off" do Framer.
class LumeQuickBuy extends HTMLElement {
  connectedCallback() {
    const limite = parseInt(this.dataset.threshold, 10) || 800;
    this.hidden = false;

    this.aoRolar = () => {
      this.dataset.ativo = lumeScrollTop() > limite ? 'true' : 'false';
    };
    this.aoRolar();
    this.soltarRolagem = lumeAoRolar(this.aoRolar);

    // Com o bloco de compra na página, a pílula compra o que foi escolhido lá
    // (cor e tamanho). Sem tamanho escolhido, leva o visitante até ele, como a Oura.
    const formCompra = document.getElementById('lume-compra-form');
    const cta = this.querySelector('.lume-quick-buy__cta');
    if (formCompra && cta) {
      cta.addEventListener('click', (evento) => {
        evento.preventDefault();
        const botao = formCompra.querySelector('.lume-compra__cta');
        if (botao && !botao.disabled) formCompra.requestSubmit(botao);
        else formCompra.closest('.lume-compra').scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' });
      });
    }
  }

  disconnectedCallback() {
    if (this.soltarRolagem) this.soltarRolagem();
  }
}

if (!customElements.get('lume-quick-buy')) customElements.define('lume-quick-buy', LumeQuickBuy);

// Caixas Fill/Percent: acordeão que avança sozinho a cada 5s, como o
// componente "Temporizador" do Framer. Clique fixa o painel escolhido.
class LumeCaixasFill extends HTMLElement {
  connectedCallback() {
    this.paineis = Array.from(this.querySelectorAll('[data-lume-caixa]'));
    if (this.paineis.length === 0) return;

    this.intervalo = (parseFloat(this.dataset.intervalo) || 5) * 1000;
    this.atual = 0;
    this.mostrar(0);

    this.paineis.forEach((painel, i) => {
      painel.addEventListener('click', () => {
        this.mostrar(i);
        this.reiniciar();
      });
      painel.addEventListener('keydown', (evento) => {
        if (evento.key !== 'Enter' && evento.key !== ' ') return;
        evento.preventDefault();
        this.mostrar(i);
        this.reiniciar();
      });
    });

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.observador = new IntersectionObserver((entradas) => {
        entradas.forEach((entrada) => {
          if (entrada.isIntersecting) this.reiniciar();
          else this.parar();
        });
      });
      this.observador.observe(this);
    }
  }

  mostrar(i) {
    this.atual = i;
    this.paineis.forEach((painel, j) => {
      const ativo = i === j;
      painel.dataset.lumeCaixa = ativo ? 'ativa' : 'contraida';
      painel.setAttribute('aria-expanded', String(ativo));
    });
  }

  reiniciar() {
    this.parar();
    this.timer = setInterval(() => {
      this.mostrar((this.atual + 1) % this.paineis.length);
    }, this.intervalo);
  }

  parar() {
    if (this.timer) clearInterval(this.timer);
  }

  disconnectedCallback() {
    this.parar();
    if (this.observador) this.observador.disconnect();
  }
}

if (!customElements.get('lume-caixas-fill')) customElements.define('lume-caixas-fill', LumeCaixasFill);

// Bloco de compra: escolher cor/tamanho troca a variante e o preço.
// Os seletores são os do Framer; quem manda no carrinho é a Shopify.
function setupCompra(raiz) {
  const escopo = raiz || document;
  escopo.querySelectorAll('.lume-compra:not([data-lume-compra-pronto])').forEach((bloco) => {
    bloco.setAttribute('data-lume-compra-pronto', '');

    // Galeria (Oura): um quadro só, arrastável, com setas e bolinhas; mostra as
    // fotos da cor escolhida mais as que valem para todas (data-lume-slide vazio).
    const trilho = bloco.querySelector('[data-lume-trilho]');
    const slides = trilho ? Array.from(trilho.querySelectorAll('[data-lume-slide]')) : [];
    const nav = bloco.querySelector('[data-lume-nav]');
    const pontos = bloco.querySelector('[data-lume-pontos]');
    let visiveis = slides;
    const atual = () => (trilho && trilho.clientWidth ? Math.round(trilho.scrollLeft / trilho.clientWidth) : 0);
    const marcar = () => {
      if (!pontos) return;
      const i = atual();
      Array.from(pontos.children).forEach((p, j) => p.setAttribute('aria-current', String(i === j)));
    };
    const irPara = (i) => {
      const alvo = visiveis[Math.max(0, Math.min(visiveis.length - 1, i))];
      if (alvo) trilho.scrollTo({ left: alvo.offsetLeft, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    };
    let corDaGaleria = null;
    const filtrarCor = (cor) => {
      if (!trilho || cor === corDaGaleria) return;
      corDaGaleria = cor;
      slides.forEach((s) => { s.hidden = Boolean(s.dataset.lumeSlide) && s.dataset.lumeSlide !== cor; });
      visiveis = slides.filter((s) => !s.hidden);
      // cor sem foto própria: melhor mostrar tudo do que um quadro vazio
      if (visiveis.length === 0) { slides.forEach((s) => { s.hidden = false; }); visiveis = slides; }
      trilho.scrollLeft = 0;
      if (nav) nav.hidden = visiveis.length < 2;
      if (pontos) {
        pontos.replaceChildren(...visiveis.map((_, i) => {
          const p = document.createElement('button');
          p.type = 'button';
          p.className = 'lume-compra__ponto';
          p.setAttribute('aria-label', `Foto ${i + 1} de ${visiveis.length}`);
          p.addEventListener('click', () => irPara(i));
          return p;
        }));
      }
      marcar();
    };
    if (trilho) {
      trilho.addEventListener('scroll', marcar, { passive: true });
      bloco.querySelectorAll('[data-lume-seta]').forEach((seta) => {
        seta.addEventListener('click', () => irPara(atual() + parseInt(seta.dataset.lumeSeta, 10)));
      });
      const corInicial = bloco.querySelector('input[name="lume-cor"]:checked');
      filtrarCor(corInicial ? corInicial.value.trim().toLowerCase() : '');
    }

    const dados = bloco.querySelector('[data-lume-variantes]');
    const campoId = bloco.querySelector('[data-lume-variant-id]');
    const alvoPreco = bloco.querySelector('[data-lume-preco]');
    if (!dados || !campoId) return;

    let variantes;
    try {
      variantes = JSON.parse(dados.textContent);
    } catch (erro) {
      return;
    }
    if (!Array.isArray(variantes) || variantes.length === 0) return;

    const radios = Array.from(bloco.querySelectorAll('.lume-compra__radio'));

    const atualizar = () => {
      // Monta a combinação escolhida na ordem das opções do produto.
      const escolha = [];
      radios.forEach((radio) => {
        if (!radio.checked) return;
        const posicao = parseInt(radio.dataset.lumeOpcao, 10);
        if (posicao) escolha[posicao - 1] = radio.value;
      });

      const variante = variantes.find((v) =>
        escolha.every((valor, i) => valor === undefined || v.options[i] === valor)
      );
      if (!variante) return;

      campoId.value = variante.id;

      if (alvoPreco && typeof variante.price === 'number') {
        // O Liquid já formatou o preço inicial; aqui seguimos o mesmo padrão.
        const reais = (centavos) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(centavos / 100);
        alvoPreco.textContent = reais(variante.price);
        // a parcela acompanha a variante escolhida, não o menor preço do produto
        const alvoParcelas = bloco.querySelector('[data-lume-parcelas]');
        if (alvoParcelas) {
          const n = parseInt(alvoParcelas.dataset.lumeParcelas, 10) || 1;
          alvoParcelas.textContent = `ou ${n}x de ${reais(Math.floor(variante.price / n))}`;
        }
      }

      const alvoCor = bloco.querySelector('[data-lume-cor-nome]');
      const corMarcada = bloco.querySelector('input[name="lume-cor"]:checked');
      if (alvoCor && corMarcada) alvoCor.textContent = corMarcada.value;
      if (corMarcada) filtrarCor(corMarcada.value.trim().toLowerCase());

      // Como na Oura: sem tamanho escolhido, o botão espera.
      const tamanhos = bloco.querySelector('[data-lume-tamanhos]');
      const modoTamanho = bloco.querySelector('input[name="lume-modo"][value="tamanho"]');
      const faltaTamanho = Boolean(tamanhos) && !(modoTamanho && modoTamanho.checked && tamanhos.querySelector('input:checked'));

      const botao = bloco.querySelector('.lume-compra__cta');
      if (botao && botao.tagName === 'BUTTON') {
        const travado = faltaTamanho || !variante.available;
        botao.disabled = travado;
        botao.setAttribute('aria-disabled', String(travado));
        const texto = botao.querySelector('[data-lume-cta-texto]');
        if (texto && variante.available) {
          texto.textContent = faltaTamanho ? botao.dataset.lumeCtaFalta : botao.dataset.lumeCta;
        }
      }
    };

    radios.forEach((radio) => radio.addEventListener('change', atualizar));
    bloco.querySelectorAll('.lume-compra__modo-radio').forEach((radio) => radio.addEventListener('change', atualizar));
    atualizar();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupCompra());
} else {
  setupCompra();
}

document.addEventListener('shopify:section:load', (evento) => setupCompra(evento.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupReveal);
} else {
  setupReveal();
}

// O editor de temas recria seções sem recarregar a página.
document.addEventListener('shopify:section:load', setupReveal);

// ---------------------------------------------------------------------------
// Palco de rolagem — reproduz o styleTransformEffect do Framer.
//
// O Framer prende quadros-chave a blocos de altura fixa dentro de um frame
// "scrolls"; conforme a página rola, ele interpola x/y/escala/rotação/opacidade
// entre esses quadros. Aqui o palco é o próprio elemento: o progresso vai de 0
// a 1 entre o topo do palco entrar e o fim dele sair, e cada filho com
// data-lume-st carrega seus quadros em JSON.
//
// A transição do Framer ("spring-physics 360 79 1") funciona como suavização
// do valor perseguido, não como uma animação com duração. Por isso aqui o
// valor aplicado persegue o alvo por interpolação a cada quadro.
// ---------------------------------------------------------------------------

const LUME_QUADRO_ZERO = { x: 0, y: 0, scale: 1, rotate: 0, opacity: 1 };

function lumeInterpolar(a, b, t) {
  const saida = {};
  for (const chave of Object.keys(LUME_QUADRO_ZERO)) {
    const de = a[chave] === undefined ? LUME_QUADRO_ZERO[chave] : a[chave];
    const ate = b[chave] === undefined ? LUME_QUADRO_ZERO[chave] : b[chave];
    saida[chave] = de + (ate - de) * t;
  }
  return saida;
}

function lumeQuadroEm(quadros, progresso) {
  if (quadros.length === 0) return { ...LUME_QUADRO_ZERO };
  if (progresso <= quadros[0].at) return lumeInterpolar(quadros[0], quadros[0], 0);

  for (let i = 0; i < quadros.length - 1; i += 1) {
    const atual = quadros[i];
    const proximo = quadros[i + 1];
    if (progresso > proximo.at) continue;
    const faixa = proximo.at - atual.at;
    const t = faixa <= 0 ? 1 : (progresso - atual.at) / faixa;
    return lumeInterpolar(atual, proximo, t);
  }

  const ultimo = quadros[quadros.length - 1];
  return lumeInterpolar(ultimo, ultimo, 0);
}

// O Framer guarda coreografias diferentes por breakpoint: na dobra do anel,
// no desktop o anel sai pela esquerda e o segundo entra pela direita; no
// celular o primeiro só desce e sobe e o segundo vem de baixo. Por isso cada
// elemento pode trazer dois conjuntos de quadros — data-lume-st (desktop) e
// data-lume-st-mob (celular) — e trocamos quando o breakpoint muda.
const LUME_CELULAR = window.matchMedia('(max-width: 809px)');

function lumeLerQuadros(el, celular) {
  const bruto = celular && el.dataset.lumeStMob ? el.dataset.lumeStMob : el.dataset.lumeSt;
  if (!bruto) return null;
  let quadros;
  try {
    quadros = JSON.parse(bruto);
  } catch (erro) {
    return null;
  }
  if (!Array.isArray(quadros) || quadros.length === 0) return null;
  quadros.sort((a, b) => a.at - b.at);
  return quadros;
}

class LumeScrollStage extends HTMLElement {
  connectedCallback() {
    const montar = () => {
      const celular = LUME_CELULAR.matches;
      this.alvos = Array.from(this.querySelectorAll('[data-lume-st]'))
        .map((el) => {
          const quadros = lumeLerQuadros(el, celular);
          if (!quadros) return null;
          return { el, quadros, atual: null };
        })
        .filter(Boolean);
    };

    montar();
    if (this.alvos.length === 0) return;

    // Ao virar o celular de lado (ou redimensionar), a coreografia troca.
    this.aoTrocarLargura = () => {
      montar();
      if (this.ligar) this.ligar();
    };
    if (LUME_CELULAR.addEventListener) {
      LUME_CELULAR.addEventListener('change', this.aoTrocarLargura);
    }

    // Quem pediu menos movimento vê o estado final, sem perseguição.
    this.reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduzido) {
      this.alvos.forEach(({ el, quadros }) => this.aplicar(el, lumeQuadroEm(quadros, 100)));
      return;
    }

    // A mola do Framer (spring 360/79/1): o valor aplicado persegue o que o
    // progresso pede com física de verdade — inércia, peso e assentamento —
    // normalizada por dt para render igual em 60Hz e 120Hz. É o que deixa o
    // movimento fluido; a antiga interpolação fixa travava nos cantos.
    this.rigidez = Math.max(1, parseFloat(this.dataset.rigidez) || 360);
    this.amortecimento = Math.max(0, parseFloat(this.dataset.amortecimento) || 79);
    this.massa = Math.max(0.05, parseFloat(this.dataset.massa) || 1);
    this.progresso = 0;
    this.rodando = false;
    this.ultimoTic = 0;

    this.medir = () => {
      const caixa = this.getBoundingClientRect();
      const percurso = caixa.height - window.innerHeight;
      if (percurso <= 0) {
        this.progresso = 0;
        return;
      }
      const andado = -caixa.top;
      this.progresso = Math.max(0, Math.min(1, andado / percurso)) * 100;
    };

    this.quadro = (agora) => {
      const dt = Math.min(1 / 30, this.ultimoTic ? (agora - this.ultimoTic) / 1000 : 1 / 60);
      this.ultimoTic = agora;
      this.medir();
      let mexeu = false;

      this.alvos.forEach((alvo) => {
        const desejado = lumeQuadroEm(alvo.quadros, this.progresso);
        if (!alvo.atual) {
          alvo.atual = { ...desejado };
          alvo.vel = { x: 0, y: 0, scale: 0, rotate: 0, opacity: 0 };
          mexeu = true;
        } else {
          for (const chave of Object.keys(LUME_QUADRO_ZERO)) {
            const desloc = alvo.atual[chave] - desejado[chave];
            const acel = (-this.rigidez * desloc - this.amortecimento * alvo.vel[chave]) / this.massa;
            alvo.vel[chave] += acel * dt;
            alvo.atual[chave] += alvo.vel[chave] * dt;
            if (Math.abs(desloc) > 0.001 || Math.abs(alvo.vel[chave]) > 0.001) mexeu = true;
          }
        }
        this.aplicar(alvo.el, alvo.atual);
      });

      if (this.visivel || mexeu) {
        this.animacao = requestAnimationFrame(this.quadro);
      } else {
        this.rodando = false;
        this.ultimoTic = 0;
      }
    };

    this.ligar = () => {
      if (this.rodando) return;
      this.rodando = true;
      this.animacao = requestAnimationFrame(this.quadro);
    };

    this.observador = new IntersectionObserver((entradas) => {
      entradas.forEach((entrada) => {
        this.visivel = entrada.isIntersecting;
        if (this.visivel) this.ligar();
      });
    });
    this.observador.observe(this);

    this.ligar();
  }

  aplicar(el, v) {
    el.style.transform =
      'translate3d(' + v.x.toFixed(2) + 'px, ' + v.y.toFixed(2) + 'px, 0) ' +
      'rotate(' + v.rotate.toFixed(2) + 'deg) ' +
      'scale(' + Math.max(0, v.scale).toFixed(4) + ')';
    // A mola pode passar de raspão dos limites; opacidade fica em [0, 1].
    el.style.opacity = Math.max(0, Math.min(1, v.opacity)).toFixed(3);
  }

  disconnectedCallback() {
    if (this.animacao) cancelAnimationFrame(this.animacao);
    if (this.observador) this.observador.disconnect();
    if (this.aoTrocarLargura && LUME_CELULAR.removeEventListener) {
      LUME_CELULAR.removeEventListener('change', this.aoTrocarLargura);
    }
  }
}

if (!customElements.get('lume-scroll-stage')) customElements.define('lume-scroll-stage', LumeScrollStage);

// ---------------------------------------------------------------------------
// Scroll Transform — o painel do Framer portado como seção da Shopify.
// A seção "Lume · Scroll Transform" injeta um JSON de configuração; cada
// vínculo aponta para um elemento existente da página (#id ou .classe) e o
// anima entre um estado inicial (From) e um final (To) conforme o alvo chega
// na tela. A transição pode ser por física (mola stiffness/damping/mass,
// como o spring do Framer) ou por tempo (duração + curva).
// ---------------------------------------------------------------------------

const LUME_FX_NEUTRO = { opacity: 1, scale: 1, rotate: 0, skewX: 0, skewY: 0, x: 0, y: 0 };
const LUME_FX_CHAVES = Object.keys(LUME_FX_NEUTRO);

function lumeFxAplicar(el, v) {
  el.style.transform =
    'translate3d(' + v.x.toFixed(2) + 'px, ' + v.y.toFixed(2) + 'px, 0) ' +
    'rotate(' + v.rotate.toFixed(2) + 'deg) ' +
    'skew(' + v.skewX.toFixed(2) + 'deg, ' + v.skewY.toFixed(2) + 'deg) ' +
    'scale(' + Math.max(0, v.scale).toFixed(4) + ')';
  el.style.opacity = Math.max(0, Math.min(1, v.opacity)).toFixed(3);
}

function lumeFxMistura(de, ate, t) {
  const saida = {};
  for (const chave of LUME_FX_CHAVES) {
    saida[chave] = de[chave] + (ate[chave] - de[chave]) * t;
  }
  return saida;
}

// Curvas do modo tempo. "expo" é a assinatura da casa (0.16, 1, 0.3, 1).
const LUME_FX_CURVAS = {
  linear: (t) => t,
  expo: (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  suave: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)
};

class LumeScrollFx extends HTMLElement {
  connectedCallback() {
    const bloco = this.querySelector('script[type="application/json"]');
    if (!bloco) return;

    let config;
    try {
      config = JSON.parse(bloco.textContent);
    } catch (erro) {
      return;
    }
    if (!config || !Array.isArray(config.vinculos)) return;

    // Menos movimento: os alvos ficam no estado natural da página, parados.
    if (REDUCED.matches) return;

    // A linha do viewport onde o percurso completa. O início é sempre a
    // borda de baixo (o alvo entrando na tela).
    const linhas = { topo: 0.1, centro: 0.5, fundo: 0.75 };
    this.linha = linhas[config.viewport] || linhas.fundo;
    this.modo = config.modo === 'tempo' ? 'tempo' : 'fisica';
    this.rigidez = Math.max(1, config.rigidez || 360);
    this.amortecimento = Math.max(0, config.amortecimento || 79);
    this.massa = Math.max(0.05, config.massa || 1);
    this.duracao = Math.max(50, config.duracao || 600) / 1000;
    this.curva = LUME_FX_CURVAS[config.curva] || LUME_FX_CURVAS.expo;

    this.itens = [];
    config.vinculos.forEach((vinculo) => {
      if (!vinculo || !vinculo.alvo) return;
      let el = null;
      try {
        el = document.querySelector(vinculo.alvo);
      } catch (erro) {
        return; // seletor inválido digitado no editor — ignora este vínculo
      }
      if (!el) return;
      const de = { ...LUME_FX_NEUTRO, ...vinculo.from };
      const ate = { ...LUME_FX_NEUTRO, ...vinculo.to };
      el.style.willChange = 'transform, opacity';
      this.itens.push({
        el,
        de,
        ate,
        atual: { ...de },
        vel: Object.fromEntries(LUME_FX_CHAVES.map((c) => [c, 0])),
        tocado: false, // modo tempo: já disparou nesta entrada
        inicioTween: 0
      });
      lumeFxAplicar(el, de);
    });
    if (this.itens.length === 0) return;

    this.ultimoTic = 0;

    this.progresso = (el) => {
      const caixa = el.getBoundingClientRect();
      const alt = window.innerHeight;
      const fim = alt * this.linha;
      const p = (alt - caixa.top) / Math.max(1, alt - fim);
      return Math.max(0, Math.min(1, p));
    };

    this.quadro = (agora) => {
      const dt = Math.min(1 / 30, this.ultimoTic ? (agora - this.ultimoTic) / 1000 : 1 / 60);
      this.ultimoTic = agora;
      let ativo = false;

      this.itens.forEach((item) => {
        const p = this.progresso(item.el);

        if (this.modo === 'tempo') {
          // Dispara ao cruzar a linha; rearma quando o alvo sai da tela.
          if (p >= 1 && !item.tocado) {
            item.tocado = true;
            item.inicioTween = agora;
          }
          if (p <= 0 && item.tocado) {
            item.tocado = false;
            item.atual = { ...item.de };
            lumeFxAplicar(item.el, item.atual);
          }
          if (item.tocado) {
            const t = Math.min(1, (agora - item.inicioTween) / 1000 / this.duracao);
            item.atual = lumeFxMistura(item.de, item.ate, this.curva(t));
            lumeFxAplicar(item.el, item.atual);
            if (t < 1) ativo = true;
          }
          return;
        }

        // Física: cada propriedade é uma mola perseguindo o valor que o
        // progresso pede — é o spring do Framer (a = (-k·x − c·v) / m).
        const alvo = lumeFxMistura(item.de, item.ate, p);
        let mexeu = false;
        for (const chave of LUME_FX_CHAVES) {
          const desloc = item.atual[chave] - alvo[chave];
          const acel = (-this.rigidez * desloc - this.amortecimento * item.vel[chave]) / this.massa;
          item.vel[chave] += acel * dt;
          item.atual[chave] += item.vel[chave] * dt;
          if (Math.abs(desloc) > 0.001 || Math.abs(item.vel[chave]) > 0.001) mexeu = true;
        }
        if (mexeu) {
          lumeFxAplicar(item.el, item.atual);
          ativo = true;
        }
      });

      if (this.visivel || ativo) {
        this.animacao = requestAnimationFrame(this.quadro);
      } else {
        this.rodando = false;
        this.ultimoTic = 0;
      }
    };

    this.ligar = () => {
      if (this.rodando) return;
      this.rodando = true;
      this.animacao = requestAnimationFrame(this.quadro);
    };

    // Roda enquanto qualquer alvo estiver na tela (com folga de uma tela
    // para o progresso começar antes do alvo aparecer).
    this.observador = new IntersectionObserver(
      (entradas) => {
        this.visiveis = this.visiveis || new Set();
        entradas.forEach((entrada) => {
          if (entrada.isIntersecting) this.visiveis.add(entrada.target);
          else this.visiveis.delete(entrada.target);
        });
        this.visivel = this.visiveis.size > 0;
        if (this.visivel) this.ligar();
      },
      { rootMargin: '100% 0px 100% 0px' }
    );
    this.itens.forEach((item) => this.observador.observe(item.el));
    this.ligar();
  }

  disconnectedCallback() {
    if (this.animacao) cancelAnimationFrame(this.animacao);
    if (this.observador) this.observador.disconnect();
    // Devolve os alvos ao estado natural: eles não pertencem à seção.
    if (this.itens) {
      this.itens.forEach((item) => {
        item.el.style.transform = '';
        item.el.style.opacity = '';
        item.el.style.willChange = '';
      });
    }
  }
}

if (!customElements.get('lume-scroll-fx')) customElements.define('lume-scroll-fx', LumeScrollFx);

// ---------------------------------------------------------------------------
// Modo de design (editor de temas): cada ajuste de campo re-renderiza a
// seção, e o palco de 300vh faz o editor perder o pé — a rolagem salta para
// depois da dobra e a tela cai no fundo escuro da seção seguinte. Guardamos
// a posição de rolagem continuamente e voltamos para ela após o re-render.
// ---------------------------------------------------------------------------

if (window.Shopify && window.Shopify.designMode) {
  let posGuardada = 0;
  let posAnterior = 0;
  const MEMORIA_MS = 300;

  setInterval(() => {
    posAnterior = posGuardada;
    posGuardada = lumeScrollTop();
  }, MEMORIA_MS);

  const restaurarRolagem = () => {
    // A mais antiga das duas: a atual pode já ser a posição pós-salto.
    const alvo = posAnterior;
    const aplicar = () => {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper && wrapper.scrollHeight > wrapper.clientHeight) {
        wrapper.scrollTop = alvo;
      } else {
        window.scrollTo(0, alvo);
      }
    };
    // Duas passadas: uma imediata e uma depois do editor terminar de rolar.
    requestAnimationFrame(aplicar);
    setTimeout(aplicar, 120);
  };

  document.addEventListener('shopify:section:load', (evento) => {
    if (evento.target.querySelector && evento.target.querySelector('lume-scroll-stage')) {
      restaurarRolagem();
    }
  });
}

// ---------------------------------------------------------------------------
// Loops contínuos — o loopEffect do Framer.
// "float": sobe e desce 5px em 3s, ida e volta (mirror).
// "spin": gira 360deg, linear, em laço.
// Os dois param quando saem da tela, como o pauseOffscreen do Framer.
// ---------------------------------------------------------------------------

function setupLoops(raiz) {
  const escopo = raiz || document;
  const alvos = escopo.querySelectorAll('[data-lume-loop]:not([data-lume-loop-pronto])');
  if (alvos.length === 0) return;

  if (REDUCED.matches) {
    alvos.forEach((el) => el.setAttribute('data-lume-loop-pronto', 'parado'));
    return;
  }

  const observador = new IntersectionObserver((entradas) => {
    entradas.forEach((entrada) => {
      entrada.target.style.animationPlayState = entrada.isIntersecting ? 'running' : 'paused';
    });
  });

  alvos.forEach((el) => {
    el.setAttribute('data-lume-loop-pronto', 'sim');
    observador.observe(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setupLoops());
} else {
  setupLoops();
}

document.addEventListener('shopify:section:load', (evento) => setupLoops(evento.target));
